import { Head } from '@inertiajs/react'
import { Card } from '../../components/core/Card.jsx'
import { Badge } from '../../components/core/Badge.jsx'
import { DataTable } from '../../components/data/DataTable.jsx'
import { EmptyState } from '../../components/feedback/EmptyState.jsx'
import { withSignedInLayout } from '../../signed_in/SignedInLayout.jsx'
import { BusinessSwitcher } from '../../signed_in/BusinessSwitcher.jsx'

// Read-only in v1. No row menu, no add, no edit, no version history — the twenty
// questions are the product's expertise, and an edited set makes a figure
// incomparable to its own past.

export default function DashboardPrompts({
  brand = null,
  businesses = [],
  businesses_included = null,
  prompts = [],
  prompt_set_version = null,
  prompt_set_created_on = null,
  unique_domains = null,
  domains_only_here = null,
}) {
  const commercial = prompts.filter((p) => p.commercial_intent)
  const other = prompts.filter((p) => !p.commercial_intent)

  return (
    <>
      <Head title="Questions" />
      <main className="mx-auto min-h-screen w-full max-w-5xl px-6 py-10">
        <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-2">
            <h1 className="page-title">Questions</h1>
            {/* The name leads, as it does on the Overview. It was left off while
                an account could show only one business; with a switcher, an
                industry and a city alone do not say whose questions these are —
                an agency can track two clients in the same trade and town. */}
            <p className="text-[13.5px] text-[color:var(--text-muted)]">
              {brand ? `${brand.name} · ${brand.industry} · ${brand.city}, ${brand.state}` : null}
              {prompt_set_version ? ` · Set ${prompt_set_version}` : null}
              {prompt_set_created_on ? ` · Created ${prompt_set_created_on}` : null}
            </p>
          </div>
          {brand && (
            <BusinessSwitcher businesses={businesses} included={businesses_included} path="/dashboard/prompts" />
          )}
        </header>

        {prompts.length === 0 ? (
          <EmptyState icon="file-text" title="No question set yet">
            The set is chosen from the industry and city when the business is added.
          </EmptyState>
        ) : (
          <div className="flex flex-col gap-6">
            <Card
              title="Questions that ask who to use"
              description="These build your visibility figure"
            >
              <div className="flex flex-col gap-4">
                <p className="max-w-[78ch] text-[13.5px] text-[color:var(--text-body)]">
                  Their answers recommend businesses, so these are the ones that can name you. Only
                  these feed your visibility figure — keeping the other questions out is what stops
                  the number being flattered. They are read for sources as well.
                </p>
                <PromptTable rows={commercial} />
              </div>
            </Card>

            <Card
              title="Questions that ask how to choose, or what things cost"
              description="These build the source map"
            >
              <div className="flex flex-col gap-4">
                <p className="max-w-[78ch] text-[13.5px] text-[color:var(--text-body)]">
                  This is how most people actually search the category, and every answer is built
                  from somebody’s pages.
                  {unique_domains != null && domains_only_here != null ? (
                    <>
                      {' '}
                      {domains_only_here} of the {unique_domains} sources we have found appear in
                      these questions and nowhere else, so this is the part of the source map the
                      visibility questions cannot reach.
                    </>
                  ) : null}
                </p>
                {/*
                  **This said "a contractor's own page"**, corrected 2026-10-09.
                  The product accepts any local business and every configured
                  vertical is selectable at setup — `CLAUDE.md` is explicit that
                  home services is a go-to-market wedge and "nothing in it may
                  gate on trade", because a restriction built from the marketing
                  plan turns away customers the product already serves. A med spa
                  reading its own dashboard was being shown somebody else's
                  product. `SourceDomain` classifies this kind as
                  `business_site`, which is the neutral word the screen should
                  have been using.

                  Accuracy rather than tone, and that outranks the nine rules.
                */}
                <p className="max-w-[78ch] text-[13.5px] text-[color:var(--text-muted)]">
                  Most of those are a business’s own page: a competitor has written the page that
                  answers the question and you have not. Any of these questions can name a business
                  now and then, as an example rather than a recommendation, so a name here is never
                  counted toward your figure.
                </p>
                <PromptTable rows={other} />
              </div>
            </Card>
          </div>
        )}
      </main>
    </>
  )
}

function PromptTable({ rows }) {
  return (
    <DataTable
      hover={false}
      columns={[
        {
          key: 'position',
          header: '#',
          width: 52,
          numeric: true,
          render: (r) => String(r.position).padStart(2, '0'),
        },
        { key: 'text', header: 'Question', render: (r) => r.text },
        {
          key: 'role',
          header: 'Contributes',
          width: 170,
          render: (r) => <Badge tone={r.commercial_intent ? 'data' : 'neutral'}>{roleFor(r)}</Badge>,
        },
      ]}
      rows={rows}
    />
  )
}

// A label states what a row reliably contributes. Naming rate tracks a question's
// wording rather than its intent, so no label promises that names never appear.
function roleFor(row) {
  if (row.commercial_intent) return 'Visibility and sources'
  if (row.intent === 'comparison' || row.intent === 'reputation') return 'Sources and examples'
  return 'Sources'
}

DashboardPrompts.layout = withSignedInLayout
